import { type FC, useEffect, useRef, useState, memo } from 'react';

import Webcam from 'react-webcam';

import { sendMultiScreenEventToIpad } from 'services/helpers/multiScreen';
import { useIpadToTvMultiScreenEventsListener } from 'services/hooks/useMultiScreenEventListener';
import { saveMedia } from 'services/networking/camera';
import { PlayerEvent } from 'shared-types/player-event';

import { CameraContainer, Container, CountDownStyle } from './Camera.style';

enum CameraMode {
  PHOTO = 'photo',
  VIDEO = 'video',
}

const Camera: FC = () => {
  const webcamRef = useRef<Webcam>(null);
  const [isRecording, setisRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [countdown, setCountdown] = useState(0);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const screenHeight = window.screen.height;
  const heightRatio = 1 / 6;
  const webCamRatio = 4 / 3;
  const height = Math.round(heightRatio * screenHeight);
  const width = Math.round(height * webCamRatio);

  useIpadToTvMultiScreenEventsListener({
    [PlayerEvent.TAKE_PHOTO]: (event: MessageEvent) => {
      const clientCode = event.data;
      startCountdown(CameraMode.PHOTO, clientCode);
    },
    [PlayerEvent.TAKE_VIDEO]: (event: MessageEvent) => {
      const clientCode = event.data;
      startCountdown(CameraMode.VIDEO, clientCode);
      sendMultiScreenEventToIpad(PlayerEvent.CAMERA_ON, CameraMode.VIDEO);
    },
    [PlayerEvent.STOP_VIDEO]: (event: MessageEvent) => {
      const clientCode = event.data;
      stopRecording(clientCode);
      sendMultiScreenEventToIpad(PlayerEvent.CAMERA_OFF, CameraMode.VIDEO);
    },
  });

  const videoConstraints = {
    width: 1920,
    height: 1080,
    facingMode: 'user',
  };

  const getCombinedStream = async () => {
    const micStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true,
    });

    const audioContext = new AudioContext();
    const micSource = audioContext.createMediaStreamSource(micStream);

    const destination = audioContext.createMediaStreamDestination();
    micSource.connect(destination);

    return new MediaStream([...destination.stream.getAudioTracks()]);
  };

  const captureImage = async (clientCode: string | null) => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (clientCode && imageSrc) {
        try {
          const imageBlob = await fetch(imageSrc).then((res) => res.blob());
          await saveMedia(clientCode, `${Date.now()}.jpg`, imageBlob);
          sendMultiScreenEventToIpad(PlayerEvent.CAMERA_OFF);
        } catch (error) {
          console.error('Error saving image', error);
        }
      }
      setisRecording(false);
      clearCamera();
    }
  };

  const startCountdown = (cameraMode: CameraMode, clientCode: string | null) => {
    setCountdown(5);
    setisRecording(true);
    setTimeout(() => {
      if (cameraMode === CameraMode.VIDEO) {
        startRecording(clientCode);
      } else if (cameraMode === CameraMode.PHOTO) {
        captureImage(clientCode);
      }
    }, 5000);
  };

  const startRecording = async (clientCode: string | null) => {
    setCountdown(0);
    setisRecording(true);
    setRecordedChunks([]);

    const combinedStream = await getCombinedStream();

    if (webcamRef.current?.stream) {
      const videoStream = webcamRef.current.stream;
      combinedStream.addTrack(videoStream.getVideoTracks()[0]);

      mediaRecorderRef.current = new MediaRecorder(combinedStream, {
        mimeType: 'video/webm',
        videoBitsPerSecond: 2500000,
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          handleDataAvailable(event);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current = null;
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
          stopRecording(clientCode);
        }
      };

      mediaRecorderRef.current.start(1000);

      timeoutIdRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          stopRecording(clientCode);
        }
      }, 300000); // 5 min
    }
  };

  const stopRecording = async (clientCode: string | null) => {
    mediaRecorderRef.current?.stop();
    if (clientCode) {
      if (recordedChunks.length === 0) {
        return;
      }
      const blob = new Blob(recordedChunks, { type: 'video/webm' });

      await saveMedia(clientCode, `${Date.now()}.webm`, blob);
    }
    sendMultiScreenEventToIpad(PlayerEvent.CAMERA_OFF);
    setisRecording(false);
    clearCamera();
  };

  const handleDataAvailable = (event: any) => {
    if (event.data) {
      setRecordedChunks((prev) => [...prev, event.data]);
    }
  };

  useEffect(() => {
    let countdownInterval: NodeJS.Timeout | undefined;

    if (countdown > 0) {
      countdownInterval = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    }

    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [countdown]);

  const clearCamera = () => {
    setisRecording(false);
    setCountdown(0);
    setRecordedChunks([]);
  };

  return (
    <>
      {isRecording && (
        <Container>
          <CameraContainer>
            {countdown > 0 && <CountDownStyle>{countdown}</CountDownStyle>}
            <Webcam
              height={height}
              width={width}
              ref={webcamRef}
              screenshotFormat='image/jpeg'
              screenshotQuality={1}
              videoConstraints={videoConstraints}
              forceScreenshotSourceSize={true}
              audio
              muted
            />
          </CameraContainer>
        </Container>
      )}
    </>
  );
};

export default memo(Camera);
